import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSalesOrders } from '@hooks/useSalesAndBilling'

const statusColors: Record<string, string> = {
  DRAFT: 'badge-warning',
  CONFIRMED: 'badge-success',
  FULFILLED: 'badge-info',
  CANCELLED: 'badge-danger',
}

export function SalesOrderListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useSalesOrders(search, status)

  const orders = data?.data?.items || []
  const total = data?.data?.total || 0
  const pageSize = data?.data?.pageSize || 20
  const totalPages = data?.data?.totalPages || 0

  const calculateTotal = (items: any[]) => {
    return items.reduce((sum, item) => sum + item.qtyOrdered * item.ratePerUnit, 0)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Orders</h1>
          <p className="text-gray-600 text-sm mt-1">
            Create orders to stores and generate invoices
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/sales/orders/create')}
          className="btn-primary"
        >
          + Create Order
        </button>
      </div>

      <div className="card">
        <div className="space-y-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              type="text"
              placeholder="Search by SO number or store..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-full"
            />

            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value)
                setPage(1)
              }}
              className="w-full"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="FULFILLED">Fulfilled</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            Failed to load sales orders. Please try again.
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {search ? 'No sales orders found.' : 'No sales orders yet.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-2 font-semibold">SO No.</th>
                    <th className="text-left px-4 py-2 font-semibold">Store</th>
                    <th className="text-left px-4 py-2 font-semibold">Date</th>
                    <th className="text-center px-4 py-2 font-semibold">Items</th>
                    <th className="text-right px-4 py-2 font-semibold">Total Amount</th>
                    <th className="text-left px-4 py-2 font-semibold">Status</th>
                    <th className="text-left px-4 py-2 font-semibold">Invoice</th>
                    <th className="text-left px-4 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order.id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-3 font-mono font-medium">
                        {order.soNumber}
                      </td>
                      <td className="px-4 py-3">{order.store.storeName}</td>
                      <td className="px-4 py-3 text-sm">
                        {new Date(order.orderDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="bg-gray-200 text-gray-800 px-2 py-1 rounded text-xs font-medium">
                          {order.items?.length || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        ₹{calculateTotal(order.items || []).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${statusColors[order.status]}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {order.invoice ? (
                          <button
                            onClick={() =>
                              navigate(`/dashboard/sales/invoices/${order.invoice.id}`)
                            }
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {order.invoice.invoiceNo}
                          </button>
                        ) : (
                          <button
                            onClick={() =>
                              navigate(
                                `/dashboard/sales/invoices/create?soId=${order.id}`
                              )
                            }
                            className="text-orange-600 hover:text-orange-800 text-xs font-medium"
                          >
                            Create Invoice
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() =>
                            navigate(
                              `/dashboard/sales/orders/${order.id}`
                            )
                          }
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Showing {(page - 1) * pageSize + 1} to{' '}
                  {Math.min(page * pageSize, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
